import { useSelector } from 'react-redux';

// Hook to check if user has permission for a specific module and action
export const usePermissions = () => {
  const { admin } = useSelector(state => state.auth);
  
  const hasPermission = (module, action) => {
    if (!admin) {
      return false;
    }
    
    if (admin.username === 'admin') {
      return true;
    }

    const modulePermissions = admin.permissions?.[module];
    if (!modulePermissions) {
      return false;
    }

    if (modulePermissions.can_manage) {
      return true;
    }

    if (action) {
      const key = `can_${action}`;
      return modulePermissions[key] === true;
    }

    return Object.values(modulePermissions).some(permission => permission === true);
  };
  
  const hasAnyPermission = (module) => {
    if (!admin) {
      return false;
    }
    
    if (admin.username === 'admin') {
      return true;
    }

    const modulePermissions = admin.permissions?.[module];
    if (!modulePermissions) {
      return false;
    }

    return modulePermissions.can_manage || Object.values(modulePermissions).some(permission => permission === true);
  };
  
  return { hasPermission, hasAnyPermission };
};

// Component wrapper to protect routes based on permissions
export const PermissionGuard = ({ module, action, children, fallback = null }) => {
  const { hasPermission, hasAnyPermission } = usePermissions();
  
  // If action is specified, check for specific permission
  if (action) {
    return hasPermission(module, action) ? children : fallback;
  }
  
  // Otherwise, check if user has any permission for the module
  return hasAnyPermission(module) ? children : fallback;
};
